import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, AvatarGroup, Box, IconButton, Link, ListItemIcon, Menu, MenuItem, Paper, Snackbar } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { levenshtein } from '../../utils/levenshtein';
import getLatestPrice from '../../../lib/stock/_getLatestPrice';
import { getPercent } from '../../utils';
import renameWatchlist from '../../../lib/watchlist/_renameWatchlist';
import deleteWatchlist from '../../../lib/watchlist/_deleteWatchlist';
import addToWatchlist from '../../../lib/watchlist/_addToWatchlist';
import RenameWatchlist from './RenameWatchlist';

const scrollbarSx = {
  '&::-webkit-scrollbar': { width: 5, height: 5 },
  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 },
  '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
};

const convertData = (data) => {
  if (!data || data.length === 0) return null;
  return data.reduce((acc, { ticker, r, p }) => {
    if (!acc[ticker]) acc[ticker] = { ticker, price_data: [] };
    acc[ticker].price_data.push({ price: p });
    return acc;
  }, {});
};

const calculateCombinedPercentageChange = (tickers) => {
  try {
    if (!tickers || typeof tickers !== 'object') return null;
    const validTickers = Object.values(tickers).filter(
      (t) => t?.price_data?.length === 2
    );
    if (validTickers.length === 0) return null;
    const sum = validTickers.reduce(
      (s, t) => s + parseFloat(getPercent(t.price_data[0]?.price, t.price_data[1]?.price)),
      0
    );
    return (sum / validTickers.length).toFixed(2);
  } catch { return null; }
};


const Row = ({ name, notes, isSystemDefault, description, tickers, handleDelete, handleCopy, handleRename, loading }) => {
  const router = useRouter();
  const [contextMenu, setContextMenu] = useState(null);
  const [watchMetrics, setWatchMetrics] = useState(null);
  const combinedPercentageChange = calculateCombinedPercentageChange(watchMetrics);
  const watchlistUrl = isSystemDefault ? `/watchlists/${name}` : `/watchlists/u/${name}`;

  useEffect(() => {
    if (tickers?.length > 0) {
      getLatestPrice(tickers, false)
        .then(data => { if (data?.getLatestPrice) setWatchMetrics(convertData(data.getLatestPrice)); })
        .catch(err => console.log('caught it!', err));
    }
  }, [tickers]);

  if (!name) return null;

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'action.hover' },
          '& td, & th': { borderBottom: '1px solid', borderColor: 'divider' },
          '&:last-child td, &:last-child th': { border: 0 },
        }}
      >
        <TableCell
          component="th"
          scope="row"
          onClick={() => router.push(watchlistUrl)}
          sx={{ py: 2, minWidth: { xs: 140, sm: 180 } }}
        >
          <Link
            href={watchlistUrl}
            underline="hover"
            color="text.primary"
            sx={{ fontWeight: 600, fontSize: '0.9rem' }}
          >
            {name}
          </Link>
          <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.72rem' }}>
            {isSystemDefault ? 'Curated by Grufity' : tickers?.length > 0 ? `${tickers.length} stock${tickers.length !== 1 ? 's' : ''}` : ''}
          </Typography>
        </TableCell>

        <TableCell align="left" sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 280 }}>
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem', color: 'text.secondary',
            }}
          >
            {description}
          </Typography>
        </TableCell>

        <TableCell align="right" sx={{ width: 100, whiteSpace: 'nowrap' }}>
          {combinedPercentageChange !== null && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600, fontSize: '0.875rem',
                  color: combinedPercentageChange >= 0 ? 'success.main' : 'error.main',
                }}
              >
                {combinedPercentageChange >= 0 ? '+' : ''}{combinedPercentageChange}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block' }}>
                Avg. return
              </Typography>
            </Box>
          )}
        </TableCell>

        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 180 }}>
          <AvatarGroup
            max={5}
            sx={{
              justifyContent: 'flex-end',
              '& .MuiAvatar-root': {
                width: 28, height: 28, border: '2px solid', borderColor: 'background.paper',
                borderRadius: '6px', fontSize: '0.75rem',
              },
              '& .MuiAvatar-colorDefault': { bgcolor: 'grey.100', color: 'text.secondary' },
            }}
          >
            {tickers?.filter(Boolean).map((ticker) => (
              <Avatar
                key={ticker}
                variant="rounded"
                alt={ticker}
                src={`https://logo.grufity.com/${ticker}.png`}
                sx={{ bgcolor: 'background.paper', '& img': { objectFit: 'contain' } }}
              >
                <ApartmentRoundedIcon sx={{ fontSize: 16 }} />
              </Avatar>
            ))}
          </AvatarGroup>
        </TableCell>

        <TableCell align="right" sx={{ width: 90, pr: { xs: 1, sm: 2 } }}>
          <Link
            href={watchlistUrl}
            underline="none"
            sx={{
              display: 'inline-block',
              fontSize: '0.8rem', fontWeight: 500, textTransform: 'none',
              borderRadius: 5, px: 2.5, py: 0.7,
              color: 'primary.main', bgcolor: 'action.hover',
              border: '1px solid', borderColor: 'divider',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            View
          </Link>
        </TableCell>

        <TableCell align="center" sx={{ width: 48, px: 0.5 }}>
          {!isSystemDefault && (
            <IconButton
              size="small"
              aria-label={`More options for ${name}`}
              onClick={(e) => {
                e.preventDefault();
                setContextMenu(contextMenu === null
                  ? { mouseX: e.clientX + 2, mouseY: e.clientY - 6 }
                  : null);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
            >
              <MoreHorizRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        slotProps={{ paper: { sx: { minWidth: 180, boxShadow: 3 } } }}
      >
        <MenuItem onClick={() => { handleCopy(name, tickers, notes, description); setContextMenu(null); }}>
          <ListItemIcon><ContentCopyRoundedIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">Duplicate</Typography>
        </MenuItem>
        <RenameWatchlist
          selectedWatchlist={name}
          selectedWatchlistDesc={description}
          handleRenameWatchlist={handleRename}
          isSystemDefault={isSystemDefault}
        />
        <MenuItem onClick={() => { handleDelete(name); setContextMenu(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteRoundedIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <Typography variant="body2">Delete</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

Row.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  isSystemDefault: PropTypes.bool.isRequired,
};

const WatchlistSection = ({ title, watchlists, handleDelete, handleCopy, handleRename, loading }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="overline"
      sx={{
        display: 'block', mb: 1.5, px: 0.5,
        fontSize: '0.7rem', fontWeight: 700,
        letterSpacing: '0.08em', color: 'text.secondary',
      }}
    >
      {title}
    </Typography>
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        ...scrollbarSx,
      }}
    >
      <Table sx={{ minWidth: 480 }} aria-label={`${title} table`}>
        <TableBody>
          {watchlists.map((d) => (
            <Row
              key={d.name}
              name={d.name}
              notes={d.notes}
              isSystemDefault={d.isSystemDefault}
              description={d.description}
              tickers={d.tickers && d.tickers !== "" ? JSON.parse(d.tickers) : []}
              handleDelete={handleDelete}
              handleCopy={handleCopy}
              handleRename={handleRename}
              loading={loading}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const ListView = ({ watchlists, accessToken, getAllWatchlists }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(watchlists);
  const [closestMatchName, setClosestMatchName] = useState(null);
  const [closestMatchLink, setClosestMatchLink] = useState(null);
  const [snackPack, setSnackPack] = useState([]);
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setFilteredData(watchlists); }, [watchlists]);

  useEffect(() => {
    if (snackPack.length && !messageInfo) {
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (snackPack.length && messageInfo && open) {
      setOpen(false);
    }
  }, [snackPack, messageInfo, open]);

  const handleSnackClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleDeleteWatchlist = async (name) => {
    if (name !== "" && accessToken) {
      await deleteWatchlist(name, accessToken).then(({ deleteWatchlist }) => {
        if (deleteWatchlist?.status === "Success") {
          getAllWatchlists();
          setSnackPack((prev) => [...prev, { message: `Removed "${name}"`, key: Date.now() }]);
        } else {
          setSnackPack((prev) => [...prev, { message: "Failed to delete watchlist", key: Date.now() }]);
        }
      }).catch(() => {
        setSnackPack((prev) => [...prev, { message: "An error occurred. Please try again.", key: Date.now() }]);
      });
    }
  };

  const handleRenameWatchlist = async (name, new_name, description) => {
    setLoading(true);
    await renameWatchlist(name, new_name, description, accessToken)
      .then(data => {
        if (data?.renameWatchlist?.status === "Success") {
          getAllWatchlists();
          setSnackPack((prev) => [...prev, { message: `Renamed to "${new_name}"`, key: Date.now() }]);
        } else {
          setSnackPack((prev) => [...prev, { message: "Failed to rename watchlist", key: Date.now() }]);
        }
        setLoading(false);
      })
      .catch(err => { console.log('caught it!', err); setLoading(false); });
  };

  const handleCopyWatchlist = async (name, tickers, notes, description) => {
    if (!name) return;
    setLoading(true);
    try {
      const data = await addToWatchlist(`Copy of ${name}`, tickers || [], notes || "", description || "", null, accessToken);
      if (data?.addToWatchlist?.status === "Success") {
        getAllWatchlists();
        setSnackPack((prev) => [...prev, { message: `Duplicated "${name}"`, key: Date.now() }]);
      } else {
        setSnackPack((prev) => [...prev, { message: "Failed to copy watchlist", key: Date.now() }]);
      }
    } catch (err) {
      console.log('caught it!', err);
      setSnackPack((prev) => [...prev, { message: "Failed to copy watchlist", key: Date.now() }]);
    } finally {
      setLoading(false);
    }
  };

  useMemo(() => {
    if (!searchTerm) {
      setClosestMatchName(null);
      setFilteredData(watchlists);
      return;
    }
    const term = searchTerm.toLowerCase();
    const searchResults = watchlists.filter(item => {
      return item.name.toLowerCase().includes(term)
        || (item.description && item.description.toLowerCase().includes(term))
        || (item.notes && item.notes.toLowerCase().includes(term))
        || item.tickers.toLowerCase().includes(term);
    });

    if (searchResults.length === 0) {
      const sorted = [...watchlists]
        .map(item => ({ item, distance: levenshtein(item.name, searchTerm) }))
        .sort((a, b) => a.distance - b.distance)
        .map(r => r.item);
      setClosestMatchName(sorted[0]?.name || null);
      setClosestMatchLink(sorted[0]?.isSystemDefault ? `/watchlists/${sorted[0]?.name}` : `/watchlists/u/${sorted[0]?.name}`);
      setFilteredData(sorted);
    } else {
      setClosestMatchName(null);
      setFilteredData(searchResults);
    }
  }, [searchTerm, watchlists]);

  const systemWatchlists = filteredData?.filter(d => d.isSystemDefault === true) || [];
  const userWatchlists = filteredData?.filter(d => d.isSystemDefault === false) || [];

  return (
    <>
      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, ticker, or description..."
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />,
              endAdornment: searchTerm ? (
                <IconButton size="small" onClick={() => { setSearchTerm(''); setClosestMatchName(null); setFilteredData(watchlists); }} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            },
          }}
          sx={{
            maxWidth: { xs: '100%', sm: 400 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2, bgcolor: 'background.paper', fontSize: '0.875rem',
              '& fieldset': { borderColor: 'divider' },
              '&:hover fieldset': { borderColor: 'text.secondary' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
            },
          }}
        />
      </Box>

      {closestMatchName && (
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          No exact match for &ldquo;<strong>{searchTerm}</strong>&rdquo;.
          Try <Link href={closestMatchLink} underline="hover" sx={{ fontWeight: 600 }}>{closestMatchName}</Link>
        </Typography>
      )}

      {/* Curated watchlists */}
      {systemWatchlists.length > 0 && (
        <WatchlistSection
          title="Curated by Grufity"
          watchlists={systemWatchlists}
          handleDelete={handleDeleteWatchlist}
          handleCopy={handleCopyWatchlist}
          handleRename={handleRenameWatchlist}
          loading={loading}
        />
      )}

      {/* User watchlists */}
      {userWatchlists.length > 0 && (
        <WatchlistSection
          title="Your Watchlists"
          watchlists={userWatchlists}
          handleDelete={handleDeleteWatchlist}
          handleCopy={handleCopyWatchlist}
          handleRename={handleRenameWatchlist}
          loading={loading}
        />
      )}

      <Snackbar
        key={messageInfo?.key}
        open={open}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        TransitionProps={{ onExited: () => setMessageInfo(undefined) }}
        message={messageInfo?.message}
        action={
          <IconButton aria-label="close" color="inherit" size="small" onClick={handleSnackClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
};

export default ListView;
